// galaxy/hii.js: HII regions (pink H-alpha knots) along the arms, as soft sprites
// with a physical radius. Positions from model.makeHII (CPU, deterministic).
// Surface brightness is distance independent; unresolved knots keep their flux.
import * as THREE from 'three';
import { makeHII, rng, BAR_PHI } from './model.js';
import { GLSL_DISK } from './shade.js';

const VERT = /* glsl */`
${GLSL_DISK}
attribute vec2 aHii;            // radius (kpc), luminosity weight
uniform vec3 uCam;
uniform float uPixAng, uGain, uMaxPx, uMinPx, uEmax;
uniform vec2 uNearD;
uniform float uBlue, uTauK;
uniform int uDustK;
uniform float uCapPx, uCapFall, uLpow, uHueVar; // r8 (1e4 / 0 / 1 / 0 = r7): size cap, overflow dimming, luminosity spread, varied colour
uniform vec3 uFar;              // r8: far fade (d0, d1, k): no dotted horizon
uniform float uGiant;           // r8: resolved-giant dimmer scale in px (10 = r7)
uniform float uCapKeep;         // r8: a capped knot keeps (true size / cap)^uCapKeep of its surface-brightness limit (0 = r7)
uniform float uCapSoft;         // r8: soft cap exponent: sz = cap * (true / cap)^uCapSoft past the cap (0 = hard cap, r7)
out vec3 vCol;
out float vSharp;
out vec4 vK;                    // r8: core sharpness, sub-knot offset (xy), sub-knot weight
void main() {
  vec3 p = position;
  float d = length(uCam - p);
  float rpx = aHii.x / max(d * uPixAng, 1e-6);          // true radius in px
  float szT = 2.6 * rpx;
  float sz = clamp(szT > uCapPx ? uCapPx * pow(szT / uCapPx, uCapSoft) : szT, uMinPx, uMaxPx); // sprite diameter (px); r8: soft size cap: knots stay small and varied, never discs
  float area = 3.1416 * rpx * rpx;                      // true area in px^2
  float S = uGain * (uLpow == 1.0 ? abs(aHii.y) : pow(abs(aHii.y), uLpow)) * 0.3; // surface brightness; r8 uLpow > 1: few bright knots, many faint
  float flux = S * area;
  float I = flux / (0.2488 * sz * sz);                  // mean over the sprite
  I = min(I, S * pow(max(szT / sz, 1.0), uCapKeep)) * smoothstep(4.0, 14.0, d / max(aHii.x, 1e-4)); // r8: capped knots stay small but keep part of their flux
  I *= 1.0 / (1.0 + pow(rpx / uGiant, 2.0)); // nearby giants: no bokeh discs, the map glow carries them
  I *= smoothstep(uNearD.x, uNearD.y, d); // no bokeh discs from knots near the camera
  I *= pow(clamp(uCapPx / max(szT, 1e-4), 0.0, 1.0), uCapFall); // r8: a knot bigger than the cap dims instead (the glow carries it)
  I *= 1.0 - (1.0 - uFar.z) * smoothstep(uFar.x, uFar.y, d);     // r8: far knots fade (no dotted horizon)
  I = I / (1.0 + I / uEmax);
  if (I < 1e-4) { gl_Position = vec4(0.0, 0.0, -2.0, 1.0); gl_PointSize = 0.0; vCol = vec3(0.0); vSharp = 0.0; vK = vec4(5.0, 0.0, 0.0, 0.0); return; }
  float tau = uDustK > 0 ? uTauK * dustTau(p, uCam, 0.0, uDustK) : 0.0;
  vec3 tr = exp(-tau * vec3(0.95, 1.0, 0.92)); // SPECTRUM: grey-violet dust (as glow.js)
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  gl_PointSize = sz;
  float hb = fract(sin(dot(p.xz, vec2(127.1, 311.7))) * 43758.5453);
  float wb = aHii.y < 0.0 ? 1.0 : smoothstep(hb, hb + 0.15, uBlue); // L < 0 or hashed share (uBlue, dive only): blue-white young cluster
  float h2 = fract(hb * 13.37 + 0.21), h3 = fract(hb * 71.3 + 0.57);
  vec3 cH = mix(vec3(1.0, 0.1, 0.46), mix(vec3(1.0, 0.05, 0.24), vec3(1.0, 0.3, 0.55), h2), uHueVar); // r8: H-alpha red .. pale pink knots
  vec3 cY = mix(vec3(0.34, 0.5, 0.76), vec3(0.5, 0.7, 1.0), uHueVar);                                  // r8: whiter blue-white clusters
  vCol = mix(cH, cY, wb) * I * tr; // SPECTRUM: hot pink #FF4FA3
  vSharp = clamp((rpx - 6.0) / 10.0, 0.0, 1.0); // only truly resolved knots get a shell (no confetti discs)
  // r8: core sharpness (clusters tighter than HII knots; tiny sprites keep the soft r7 core so no flux is lost between pixel centres)
  float kc = mix(5.0, mix(9.0, 20.0, wb) * (0.75 + 0.5 * h3), smoothstep(3.0, 9.0, sz));
  float an = 6.2832 * h2;
  vK = vec4(kc, 0.42 * vec2(cos(an), sin(an)) * smoothstep(4.0, 10.0, sz), (1.0 - wb) * 0.45 * h3);
}`;
const FRAG = /* glsl */`
in vec3 vCol;
in float vSharp;
in vec4 vK;
uniform float uCrisp;
void main() {
  vec2 q = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(q, q);
  if (r2 > 1.0) discard;
  // resolved knots get a lumpy shell; unresolved ones are Gaussian dots
  float g = 1.44 * (exp(-5.0 * r2) + 0.2 * exp(-1.8 * r2)) * (1.0 - r2); // bright core + soft halo, same flux
  float shell = smoothstep(1.0, 0.55, r2) * (0.55 + 0.45 * smoothstep(0.0, 0.5, r2));
  float gg = mix(g, shell, vSharp * 0.6);
  if (uCrisp > 0.0) {
    // r8: crisp, varied knot: a tight core (+ an offset sub-knot on bigger HII sprites) over a faint compact halo; same flux as above
    float a = vK.x;
    vec2 q2 = q - vK.yz;
    float core = mix(exp(-a * r2), 1.6 * exp(-1.6 * a * dot(q2, q2)), vK.w) * a / (1.0 - exp(-a));
    gg = mix(gg, 0.3166 * (0.85 * core + 0.15 * 3.609 * exp(-3.5 * r2)), uCrisp);
  }
  gl_FragColor = vec4(vCol * gg, 1.0);
}`;

export function makeHIIPoints(count, maps, given) {
  const list = given || makeHII(count, rng(20190131));
  const n = list.length;
  const pos = new Float32Array(n * 3), at = new Float32Array(n * 2);
  list.forEach((h, i) => { pos.set([h.x, h.y, h.z], i * 3); at.set([h.r, h.L], i * 2); });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aHii', new THREE.BufferAttribute(at, 2));
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 30);
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uMapA: { value: maps.A.texture }, uMapB: { value: maps.B.texture },
      uCam: { value: new THREE.Vector3() }, uPixAng: { value: 0.001 }, uGain: { value: 1 },
      uMaxPx: { value: 48 }, uMinPx: { value: 2.5 }, uEmax: { value: 3 }, uDustK: { value: 2 }, uNearD: { value: new THREE.Vector2(1, 3) }, uBlue: { value: 0 }, uTauK: { value: 1 },
      uCapPx: { value: 1e4 }, uCapFall: { value: 0 }, uCrisp: { value: 0 }, uLpow: { value: 1 }, uHueVar: { value: 0 }, uGiant: { value: 10 }, uCapKeep: { value: 0 }, uCapSoft: { value: 0 }, uFar: { value: new THREE.Vector3(50, 60, 1) }, // r8
    },
    vertexShader: VERT, fragmentShader: FRAG,
    transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  return { points, U: mat.uniforms, count: n, dispose() { geo.dispose(); mat.dispose(); } };
}

// SPECTRUM dive end (artistic, after the nuclear rings of NGC 1097 / NGC 1300): star-forming hot spots strung
// along map.js's nuclear ring (0.3 x 0.19 kpc in the bar frame): pink HII knots (L > 0) and blue-white young
// clusters (L < 0), a little above the mid-plane so the ring's own dust doesn't swallow them. Drawn only in the dive.
export function makeRingSparks(count, rand) {
  const out = [], c = Math.cos(BAR_PHI), s = Math.sin(BAR_PHI);
  const gauss = () => Math.sqrt(-2 * Math.log(rand() + 1e-9)) * Math.cos(2 * Math.PI * rand());
  const spots = [];
  for (let k = 0; k < 10; k++) spots.push(rand() * Math.PI * 2);
  for (let i = 0; i < count; i++) {
    const th = i % 4 === 3 ? rand() * Math.PI * 2 : spots[i % spots.length] + gauss() * 0.16;
    const rr = 1 + gauss() * 0.05, bu = 0.3 * rr * Math.cos(th), bv = 0.19 * rr * Math.sin(th);
    const L = 0.5 + 0.9 * Math.pow(1 - 0.92 * rand(), -0.8);
    out.push({ x: bu * c - bv * s, y: 0.004 + Math.abs(gauss()) * 0.01, z: bu * s + bv * c, r: 0.004 + 0.008 * rand(), L: rand() < 0.4 ? -L : L });
  }
  return out;
}
